import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Trash2, Upload, Eye, EyeOff, Plus, Loader2 } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { GALLERY_CATEGORIES } from "@/lib/data";

const input = "w-full rounded-lg border border-[#2C2C2C]/20 bg-white px-3 py-2 text-sm outline-none focus:border-[#C9A84C]";
const EMPTY = { media_type: "image", url: "", category: "mariages", caption: "", alt: "", is_real: false, published: true, order: 0 };

export default function AdminMedias() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/gallery", { params: { include_hidden: true } }).then((r) => setItems(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, url: r.data.url, media_type: file.type.startsWith("video") ? "video" : "image" }));
      toast.success("Fichier téléversé");
    } catch { toast.error("Échec du téléversement"); }
    finally { setUploading(false); }
  };

  const add = async () => {
    if (!form.url) { toast.error("Ajoutez un fichier ou une URL"); return; }
    setSaving(true);
    try {
      await api.post("/gallery", { ...form, order: items.length });
      setForm(EMPTY);
      toast.success("Média ajouté");
      load();
    } catch { toast.error("Échec de l'ajout"); }
    finally { setSaving(false); }
  };

  const update = async (item, patch) => {
    try { await api.put(`/gallery/${item.id}`, { ...item, ...patch }); load(); }
    catch { toast.error("Échec de la mise à jour"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce média ?")) return;
    try { await api.delete(`/gallery/${id}`); toast.success("Supprimé"); load(); }
    catch { toast.error("Échec"); }
  };

  return (
    <div data-testid="admin-medias">
      <h1 className="font-serif text-3xl text-[#2C2C2C]">Galerie & médias</h1>
      <p className="mt-1 text-sm text-[#2C2C2C]/60">Ajoutez, classez et publiez vos photos et vidéos de réalisations.</p>

      {/* Add form */}
      <div className="mt-6 rounded-xl border border-[#2C2C2C]/10 bg-white p-6">
        <h2 className="mb-4 font-serif text-lg text-[#2C2C2C]">Ajouter un média</h2>
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <label className="mb-1 block text-sm font-medium text-[#2C2C2C]">Téléverser un fichier (image ou vidéo)</label>
            <label className="flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed border-[#2C2C2C]/30 bg-[#FAF6EF] py-6 text-sm text-[#2C2C2C]/70 hover:border-[#C9A84C]">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {uploading ? "Téléversement…" : "Choisir un fichier (max 15 Mo)"}
              <input type="file" accept="image/*,video/*" className="hidden" data-testid="media-upload-input" onChange={(e) => upload(e.target.files[0])} />
            </label>
            <p className="mt-2 text-xs text-[#2C2C2C]/50">… ou collez une URL :</p>
            <input className={input} placeholder="https://…" data-testid="media-url-input" value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} />
            {form.url && form.media_type === "image" && <img src={mediaUrl(form.url)} alt="aperçu" className="mt-3 h-32 rounded-lg object-cover" />}
          </div>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2C2C]">Catégorie</label>
              <select className={input} data-testid="media-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {GALLERY_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-[#2C2C2C]">Type</label>
              <select className={input} value={form.media_type} onChange={(e) => setForm({ ...form, media_type: e.target.value })}>
                <option value="image">Image</option>
                <option value="video">Vidéo</option>
              </select>
            </div>
            <input className={input} placeholder="Légende" data-testid="media-caption" value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} />
            <input className={input} placeholder="Texte alternatif (SEO / accessibilité)" data-testid="media-alt" value={form.alt} onChange={(e) => setForm({ ...form, alt: e.target.value })} />
            <label className="flex items-center gap-2 text-sm text-[#2C2C2C]/80">
              <input type="checkbox" className="h-4 w-4 accent-[#2C2C2C]" checked={form.is_real} onChange={(e) => setForm({ ...form, is_real: e.target.checked })} />
              Vraie réalisation Afro Regal (sinon : visuel d'ambiance)
            </label>
          </div>
        </div>
        <button onClick={add} disabled={saving} data-testid="media-add-btn" className="mt-4 flex items-center gap-2 rounded-lg bg-[#2C2C2C] px-5 py-2.5 text-sm text-white hover:bg-[#C9A84C] hover:text-[#2C2C2C] disabled:opacity-60"><Plus size={15} /> Ajouter à la galerie</button>
      </div>

      {/* List */}
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.length === 0 && <p className="text-[#2C2C2C]/50" data-testid="media-empty">Aucun média pour l'instant.</p>}
        {items.map((it) => (
          <div key={it.id} className="overflow-hidden rounded-xl border border-[#2C2C2C]/10 bg-white" data-testid={`media-card-${it.id}`}>
            <div className="h-40 bg-[#2C2C2C]/5">
              {it.media_type === "video" ? <video src={mediaUrl(it.url)} className="h-full w-full object-cover" /> : <img src={mediaUrl(it.url)} alt={it.alt} className="h-full w-full object-cover" />}
            </div>
            <div className="space-y-2 p-3 text-sm">
              <p className="truncate font-medium text-[#2C2C2C]">{it.caption || "(sans légende)"}</p>
              <select className="w-full rounded border border-[#2C2C2C]/20 px-2 py-1 text-xs" value={it.category} onChange={(e) => update(it, { category: e.target.value })}>
                {GALLERY_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
              <div className="flex items-center justify-between">
                <span className={`text-xs ${it.is_real ? "text-green-700" : "text-[#C9A84C]"}`}>{it.is_real ? "Réalisation" : "Ambiance"}</span>
                <div className="flex gap-2">
                  <button onClick={() => update(it, { published: !it.published })} data-testid={`media-toggle-${it.id}`} title={it.published ? "Dépublier" : "Publier"} className="text-[#2C2C2C]/70 hover:text-[#2C2C2C]">{it.published ? <Eye size={16} /> : <EyeOff size={16} />}</button>
                  <button onClick={() => remove(it.id)} data-testid={`media-delete-${it.id}`} className="text-red-600"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
